<template>
  <div class="modal fade ai-report-wizard" id="aiReportModal" tabindex="-1" aria-labelledby="aiReportModalLabel" aria-hidden="true">
    <div class="modal-dialog modal-lg">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title" id="aiReportModalLabel">
            Filtro com Inteligência Artificial
          </h5>
          <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
        </div>
        <div class="modal-body">
          <!-- Loading State -->
          <div v-if="isLoading" class="text-center py-4">
            <div class="spinner-border text-primary" role="status">
              <span class="visually-hidden">Carregando...</span>
            </div>
            <p class="mt-2">Processando...</p>
          </div>

          <!-- Error State -->
          <div v-else-if="error" class="alert alert-danger" role="alert">
            <strong>Erro:</strong> {{ error }}
          </div>

          <!-- Final Report -->
          <div v-else-if="showFinalReport" class="ai-report-final">
            <div class="alert alert-success" role="alert">
              <h6><i class="fas fa-check-circle"></i> Relatório gerado com sucesso!</h6>
              <p class="mb-0">A IA selecionou {{ selectedResults?.length || 0 }} resultados baseados nas suas preferências e marcou {{ markedCount }} itens no banco de dados.</p>
            </div>
            
            <div class="report-summary mb-4" v-if="answeredQuestions && answeredQuestions.length > 0">
              <h6>Resumo das características selecionadas:</h6>
              <div class="row">
                <div class="col-md-6" v-for="(answer, index) in answeredQuestions" :key="index">
                  <div class="characteristic-item mb-2">
                    <strong>{{ answer.question }}:</strong>
                    <span class="text-primary">{{ answer.answer }}</span>
                  </div>
                </div>
              </div>
            </div>

            <div class="selected-results" v-if="selectedResults && selectedResults.length > 0">
              <h6>Resultados selecionados pela IA:</h6>
              <div class="table-responsive">
                <table class="table table-sm">
                  <thead>
                    <tr>
                      <th>Produto</th>
                      <th>Preço</th>
                      <th>Fonte</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="result in selectedResults" :key="result.id">
                      <td>{{ result.description }}</td>
                      <td>{{ formatCurrency(result.price) }}</td>
                      <td>{{ result.source }}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
            
            <div v-else-if="selectedResults && selectedResults.length === 0" class="alert alert-info">
              <p class="mb-0">Nenhum resultado foi selecionado pela IA baseado nas suas preferências.</p>
            </div>
          </div>

          <!-- Wizard Steps -->
          <div v-else class="ai-wizard">
            <!-- Current Question -->
            <div class="question-container" v-if="currentQuestion">
              <h6 class="question-title mb-3">
                <i class="fas fa-question-circle text-primary"></i>
                {{ currentQuestion.question }}
              </h6>
              
              <div class="options-container">
                <div class="row">
                  <div class="col-md-6" v-for="option in currentQuestion.options" :key="option.value">
                    <button 
                      type="button" 
                      class="btn btn-outline-primary w-100 mb-2 option-btn"
                      @click="selectOption(option)"
                      :class="{ 'active': selectedOption === option.value }"
                    >
                      <i class="fas fa-check-circle me-2" v-if="selectedOption === option.value"></i>
                      {{ option.label }}
                    </button>
                  </div>
                </div>
              </div>

              <!-- Navigation Buttons -->
              <div class="wizard-navigation mt-4">
                <button 
                  type="button" 
                  class="btn btn-secondary me-2"
                  @click="previousQuestion"
                  :disabled="currentStep === 0"
                >
                  <i class="fas fa-chevron-left"></i> Anterior
                </button>
                
                <button 
                  type="button" 
                  class="btn btn-outline-secondary me-2"
                  @click="skipQuestion"
                >
                  <i class="fas fa-forward"></i> Pular
                </button>
                
                <button 
                  type="button" 
                  class="btn btn-primary"
                  @click="nextQuestion"
                  :disabled="!selectedOption"
                >
                  <span v-if="currentStep < questions.length - 1">
                    Próximo <i class="fas fa-chevron-right"></i>
                  </span>
                  <span v-else>
                    Avançar <i class="fas fa-chevron-right"></i>
                  </span>
                </button>
              </div>
            </div>

            <!-- Welcome Screen -->
            <div v-else class="welcome-screen text-center">
              <div class="mb-4">
                <i class="fas fa-robot fa-3x text-primary mb-3"></i>
                <h5>Olá! Vou ajudá-lo a refinar sua pesquisa</h5>
                <p class="text-muted">
                  Analisarei uma amostra de resultados encontrados para criar perguntas específicas 
                  que ajudem a entender melhor o que você está procurando. Farei uma pergunta por vez
                  sobre unidade de medida, faixa de preço e outras características importantes. Depois 
                  selecionarei os resultados mais relevantes baseados nas suas respostas.
                </p>
              </div>
              <button type="button" class="btn btn-primary" @click="startWizard">
                <i class="fas fa-play"></i> Começar
              </button>
            </div>
          </div>
        </div>
        
        <div class="modal-footer" v-if="showFinalReport">
          <button type="button" class="btn btn-success" @click="applyReport">
            <i class="fas fa-eye"></i> Ver Resultados Selecionados
          </button>
          <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">
            Fechar
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'AiReportWizard',
  props: {
    searchId: {
      type: Number,
      required: true
    },
    searchTerm: {
      type: String,
      required: true
    }
  },
  data() {
    return {
      isLoading: false,
      error: null,
      showFinalReport: false,
      currentStep: -1, // -1 for welcome screen
      selectedOption: null,
      answers: [],
      selectedResults: [],
      questions: [],
      markedCount: 0,
      modal: null
    }
  },
  computed: {
    currentQuestion() {
      if (!this.questions || !Array.isArray(this.questions) || this.currentStep < 0 || this.currentStep >= this.questions.length) {
        return null;
      }
      return this.questions[this.currentStep] || null;
    },
    answeredQuestions() {
      if (!this.answers || !Array.isArray(this.answers) || !this.questions || !Array.isArray(this.questions)) {
        return [];
      }
      return this.answers.map((answer, index) => ({
        question: this.questions[index]?.question || '',
        answer: answer ? (answer.label || answer.value || '') : 'Pergunta ignorada'
      })).filter(item => item.answer !== 'Pergunta ignorada');
    }
  },
  methods: {
    show() {
      this.modal.show();
    },
    
    hide() {
      this.modal.hide();
    },
    
    reset() {
      this.isLoading = false;
      this.error = null;
      this.showFinalReport = false;
      this.currentStep = -1;
      this.selectedOption = null;
      this.answers = [];
      this.selectedResults = [];
      this.questions = [];
      this.markedCount = 0;
    },
    
    async startWizard() {
      this.isLoading = true;
      try {
        // Generate first question based on the search term
        const questions = await this.generateQuestions();
        if (questions && questions.length > 0) {
          this.questions = [questions[0]]; // Only take the first question
          this.currentStep = 0;
          this.selectedOption = null;
        } else {
          this.error = 'Não foi possível gerar perguntas. Tente novamente.';
        }
      } catch (error) {
        this.error = 'Erro ao gerar primeira pergunta: ' + error.message;
      } finally {
        this.isLoading = false;
      }
    },
    
    async generateQuestions(previousAnswers = []) {
      const response = await fetch(`/product_searches/${this.searchId}/ai_questions.json`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').getAttribute('content')
        },
        body: JSON.stringify({
          search_term: this.searchTerm,
          previous_answers: previousAnswers
        })
      });
      
      if (!response.ok) {
        throw new Error('Erro ao comunicar com o servidor');
      }
      
      const data = await response.json();
      return data.questions;
    },
    
    selectOption(option) {
      this.selectedOption = option.value;
    },
    
    nextQuestion() {
      if (!this.selectedOption || !this.currentQuestion || !this.currentQuestion.options) return;
      
      // Save the answer
      const selectedOptionObj = this.currentQuestion.options.find(opt => opt.value === this.selectedOption);
      if (selectedOptionObj) {
        this.answers.push(selectedOptionObj);
      }
      
      this.proceedToNextQuestion();
    },
    
    skipQuestion() {
      // Add a null answer to keep track of skipped questions
      this.answers.push(null);
      this.proceedToNextQuestion();
    },
    
    async proceedToNextQuestion() {
      // Check if we should generate more questions or finish
      if (this.answers.length >= 5) {
        // Generate final report after 5 questions
        this.generateReport();
        return;
      }
      
      // Generate next question based on answers so far
      this.isLoading = true;
      try {
        // Build previous answers with question context
        const previousAnswers = this.answers.map((answer, index) => ({
          question: this.questions[index]?.question || '',
          ...answer
        }));
        
        const newQuestions = await this.generateQuestions(previousAnswers);
        if (newQuestions && newQuestions.length > 0) {
          // Add the new question to our questions array
          this.questions.push(newQuestions[0]); // Only take the first question since we generate 1 at a time
          this.currentStep++;
          this.selectedOption = null;
        } else {
          // No more questions, generate report
          this.generateReport();
        }
      } catch (error) {
        this.error = 'Erro ao gerar próxima pergunta: ' + error.message;
      } finally {
        this.isLoading = false;
      }
    },
    
    previousQuestion() {
      if (this.currentStep > 0) {
        this.currentStep--;
        if (this.answers && this.answers.length > 0) {
          this.answers.pop();
        }
        // Remove the last question since we generate one at a time
        if (this.questions && this.questions.length > 1) {
          this.questions.pop();
        }
        this.selectedOption = null;
      }
    },
    
    async generateReport() {
      this.isLoading = true;
      try {
        const response = await fetch(`/product_searches/${this.searchId}/ai_report.json`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').getAttribute('content')
          },
          body: JSON.stringify({
            search_term: this.searchTerm,
            answers: this.answers
          })
        });
        
        if (!response.ok) {
          throw new Error('Erro ao gerar relatório');
        }
        
        const data = await response.json();
        this.selectedResults = data.selected_results || [];
        this.markedCount = data.marked_count || 0;
        this.showFinalReport = true;
      } catch (error) {
        this.error = 'Erro ao gerar relatório: ' + error.message;
      } finally {
        this.isLoading = false;
      }
    },
    
    applyReport() {
      // Redirect to AI tab to show the selected results
      window.location.href = '?filter=ai';
      this.hide();
    },
    
    formatCurrency(value) {
      if (value === null || value === undefined || isNaN(value)) {
        return 'R$ 0,00';
      }
      return new Intl.NumberFormat('pt-BR', {
        style: 'currency',
        currency: 'BRL'
      }).format(value);
    }
  },
  mounted() {
    // Initialize Bootstrap modal
    const modalElement = document.getElementById('aiReportModal');
    this.modal = new bootstrap.Modal(modalElement);
    
    // Reset when modal is hidden
    modalElement.addEventListener('hidden.bs.modal', () => {
      this.reset();
    });
  }
}
</script>